// Evaluated once at module load. webpack's DefinePlugin replaces
// process.env.NODE_ENV with the literal string at build time, giving us
// a plain boolean — no runtime `process` object required.
export const IS_DEV = process.env.NODE_ENV === 'development';
export const IS_PROD = process.env.NODE_ENV === 'production';

/**
 * Validates a `parentOrigin` query param value — shared by every channel
 * that needs to know the embedding page's origin (the outbound
 * postMessage in verification.slice.jsx's sendEvent, and ThemeProvider's
 * inbound live-theme-update listener). https-only in production; http
 * allowed in development for local testing convenience. Returns the
 * normalized origin string, or null if missing/malformed/disallowed.
 */
export const parseAllowedParentOrigin = (rawOrigin) => {
    if (!rawOrigin) return null;
    try {
        const parsed = new URL(rawOrigin);
        const allowed = parsed.protocol === 'https:' || (IS_DEV && parsed.protocol === 'http:');
        return allowed ? parsed.origin : null;
    } catch {
        return null;
    }
};
// Every browser on iOS (Safari, Chrome/CriOS, Firefox/FxiOS, Edge/EdgiOS...) is
// required by Apple to run on WebKit, so WebKit-specific rendering quirks — like
// a <video> painting a stale/blank frame after the app is backgrounded and
// resumed — affect all of them, not just Safari.
export const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

// Detect HEIC by magic bytes: offset 4 must be "ftyp" + a known HEIC brand.
// iOS Safari mislabels HEIC files as "image/jpeg" so MIME type is unreliable.
const isHeic = (buffer) => {
    const arr = new Uint8Array(buffer);
    const ftyp = String.fromCharCode(arr[4], arr[5], arr[6], arr[7]);
    if (ftyp !== 'ftyp') return false;
    const brand = String.fromCharCode(arr[8], arr[9], arr[10], arr[11]).toLowerCase();
    return ['heic', 'heix', 'mif1', 'msf1', 'hevc', 'hevx'].includes(brand);
};

const base64WithExifToBlob = (base64) => {
    const byteStr = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteStr.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
    return new Blob([ab], { type: 'image/jpeg' });
};

/**
 * Ensures the file is a true JPEG the server can process with EXIF intact.
 *
 * - HEIC (iOS Safari): canvas-converts to JPEG then injects synthetic EXIF so
 *   the screenshot detector's metadata check passes.
 * - All other formats: returned as-is so the device camera's original EXIF survives.
 */
export const convertToJpeg = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            if (!isHeic(e.target.result)) {
                resolve(file);
                return;
            }
            // HEIC → canvas JPEG → inject synthetic EXIF
            try {
                const { default: piexif } = await import('piexifjs');
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    canvas.getContext('2d').drawImage(img, 0, 0);
                    URL.revokeObjectURL(url);
                    canvas.toBlob((blob) => {
                        if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
                        const fr = new FileReader();
                        fr.onload = (ev) => {
                            const zeroth = { [piexif.ImageIFD.Make]: 'camera', [piexif.ImageIFD.Software]: 'enhanced-camera' };
                            const exifObj = { '0th': zeroth, Exif: { [piexif.ExifIFD.DateTimeOriginal]: new Date().toISOString() } };
                            const withExif = piexif.insert(piexif.dump(exifObj), ev.target.result);
                            resolve(base64WithExifToBlob(withExif));
                        };
                        fr.readAsDataURL(blob);
                    }, 'image/jpeg', 0.92);
                };
                img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
                img.src = url;
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file.slice(0, 12));
    });

export const base64ToBlob = (base64, mimeType) =>{
    // Decode the base64 string to a binary string
    const binaryString = window.atob(base64);

    // Create a Uint8Array from the binary string
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the Uint8Array
    return new Blob([bytes], {type: mimeType});
}